import React from "react";

/**
 * NecklaceVisualization Component - Shows scale as circular necklace representation
 * Visualizes Lyndon word structure and rotational modes
 * 
 * @param {Array} scale - Array of scale intervals
 * @param {number} mode - Current mode (rotation) of the scale
 * @param {number} superscaleIndex - Superscale index for Lyndon word labeling
 */
const NecklaceVisualization = ({ scale, mode = 1, superscaleIndex }) => {
  // Calculate total scale length for proper positioning
  const totalSemitones = scale.reduce((sum, interval) => sum + interval, 0);
  const beadCount = scale.length + 1; // Include root note
  
  // SVG dimensions
  const size = 200;
  const center = size / 2;
  const radius = 70;
  const beadRadius = 8;
  
  // Build positions using chromatic clock positions (0-11 semitones)
  const positions = [];
  let cumulative = 0;
  
  // First position is the mode root at 0 semitones (12 o'clock)
  positions.push({ 
    semitones: 0, 
    isModeRoot: true,
    isOriginalRoot: mode === 1, // Only true if we're in mode 1
    interval: null, 
    clockPosition: 0 
  });
  
  // Calculate where the original root appears in this mode
  // If mode = 10, original root is at position (12 - 10) = 2
  const totalPositions = scale.length + 1; // Include the octave
  const originalRootPosition = mode === 1 ? 0 : (totalPositions - mode) % totalPositions;
  
  // Add each subsequent scale degree at proper chromatic positions
  scale.forEach((interval, index) => {
    cumulative += interval;
    const positionIndex = index + 1;
    
    positions.push({ 
      semitones: cumulative, 
      isModeRoot: false,
      isOriginalRoot: positionIndex === originalRootPosition,
      interval: interval,
      intervalSize: interval,
      clockPosition: cumulative % 12
    });
  });
  
  // Convert clock positions to angles (12-tone chromatic circle)
  const angleStep = (2 * Math.PI) / 12;
  
  // All notes are uniform circles, intervals shown in connection weight
  const noteRadius = 8;
  const noteStrokeWidth = 2;
  

  // Get connection stroke width based on interval size
  const getConnectionStyle = (intervalSize) => {
    if (intervalSize === 1) return { strokeWidth: 2, opacity: 0.5, color: 'rgba(165, 153, 140, 0.6)' };
    if (intervalSize === 2) return { strokeWidth: 5, opacity: 0.85, color: 'rgba(74, 69, 62, 0.8)' };
    return { strokeWidth: 7, opacity: 1.0, color: 'rgba(74, 69, 62, 1.0)' }; // 3+ semitones
  };
  
  const renderNote = (position, index) => {
    // Place notes at chromatic clock positions (0-11 semitones)
    const angle = position.clockPosition * angleStep - (Math.PI / 2); // Start at 12 o'clock
    const x = center + radius * Math.cos(angle);
    const y = center + radius * Math.sin(angle);
    
    // Determine note class based on what type of root it is
    // When mode === 1, both isModeRoot and isOriginalRoot are true for the first note
    // In that case, only show the original root (orange), not the mode root (green)
    const noteClass = position.isOriginalRoot && position.isModeRoot ? 'necklace-root' :
                     position.isModeRoot ? 'necklace-mode-root' : 
                     position.isOriginalRoot ? 'necklace-root' : 'necklace-note';
    
    return (
      <circle
        key={`note-${index}`}
        cx={x}
        cy={y}
        r={noteRadius}
        className={noteClass}
      />
    );
  };
  
  const renderConnections = () => {
    return positions.map((position, index) => {
      const nextIndex = (index + 1) % positions.length;
      const nextPosition = positions[nextIndex];
      
      // Get interval size - this is the interval FROM this position TO the next
      const intervalSize = index < scale.length ? scale[index] : scale[scale.length - 1];
      
      // Calculate positions using chromatic clock positions
      const angle1 = position.clockPosition * angleStep - (Math.PI / 2);
      const angle2 = nextPosition.clockPosition * angleStep - (Math.PI / 2);
      
      const x1 = center + radius * Math.cos(angle1);
      const y1 = center + radius * Math.sin(angle1);
      const x2 = center + radius * Math.cos(angle2);
      const y2 = center + radius * Math.sin(angle2);
      
      // Calculate arc direction - always go clockwise around the circle
      const deltaAngle = (nextPosition.clockPosition - position.clockPosition + 12) % 12;
      const largeArc = deltaAngle > 6 ? 1 : 0;
      const sweep = 1; // Always clockwise
      
      // Style based on interval size
      const connectionStyle = getConnectionStyle(intervalSize);
      
      return (
        <path
          key={`connection-${index}`}
          d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} ${sweep} ${x2} ${y2}`}
          className="necklace-connection"
          fill="none"
          stroke={connectionStyle.color}
          strokeWidth={connectionStyle.strokeWidth}
          opacity={connectionStyle.opacity}
        />
      );
    });
  };
  
  // Get ordinal suffix for mode number
  const getOrdinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  
  return (
    <div className="necklace-container">
      <div className="necklace-label">Necklace Visualization</div>
      <div className="necklace-content">
        <div className="necklace-info">
          {/*
          <div className="necklace-details">
            {mode > 1 ? (
              <p className="necklace-description">
                Superscale <strong>{superscaleIndex}</strong>, <strong>{getOrdinal(mode)}</strong> Mode
              </p>
            ) : (
              <p className="necklace-description">
                Superscale <strong>{superscaleIndex}</strong>
              </p>
            )}
          </div>
          */}
          <div className="necklace-legend">
            <div className="legend-row">
              <div className="legend-item">
                <div className="legend-symbol necklace-mode-symbol"></div>
                <span>Mode Root</span>
              </div>
              {mode > 1 && (
                <div className="legend-item">
                  <div className="legend-symbol necklace-root-symbol"></div>
                  <span>Tonic</span>
                </div>
              )}
            </div>
            
            <div className="legend-row">
              <div className="legend-item">
                <div className="legend-symbol interval-stroke-1"></div>
                <span>1 step</span>
              </div>
              <div className="legend-item">
                <div className="legend-symbol interval-stroke-2"></div>
                <span>2 steps</span>
              </div>
              <div className="legend-item">
                <div className="legend-symbol interval-stroke-3"></div>
                <span>3 steps</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="necklace-visualization">
          <svg 
        width={size} 
        height={size} 
        viewBox={`0 0 ${size} ${size}`}
        className="necklace-svg"
      >
        {/* Clock face reference (12-tone circle) */}
        <circle 
          cx={center} 
          cy={center} 
          r={radius + 15} 
          fill="none" 
          stroke="rgba(0,0,0,0.1)" 
          strokeWidth="1"
          strokeDasharray="2,2"
        />
        
        {/* Clock tick marks (0-11 semitones) */}
        {Array.from({ length: 12 }, (_, i) => {
          const angle = i * angleStep - (Math.PI / 2);
          const innerRadius = radius - 8;
          const outerRadius = radius - 2;
          const x1 = center + innerRadius * Math.cos(angle);
          const y1 = center + innerRadius * Math.sin(angle);
          const x2 = center + outerRadius * Math.cos(angle);
          const y2 = center + outerRadius * Math.sin(angle);
          
          return (
            <line 
              key={`tick-${i}`}
              x1={x1} 
              y1={y1} 
              x2={x2} 
              y2={y2} 
              stroke="rgba(0,0,0,0.25)" 
              strokeWidth="1.5"
              className="clock-tick"
            />
          );
        })}
        
        {/* Connections between notes (intervals) */}
        {renderConnections()}
        
        {/* Notes (uniform circles) - exclude the last position since it's the octave (same as first note) */}
        {positions.slice(0, -1).map(renderNote)}
        
        {/* Center indicator */}
        <circle 
          cx={center} 
          cy={center} 
          r="3" 
          fill="rgba(0,0,0,0.3)"
        />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default NecklaceVisualization;